import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
} from "@mui/material";
import { useGetSettlementPoolBoostsQuery } from "Api/Queries/Settlements/useGetSettlementPoolBoostsQuery";
import { StardustBoostCard } from "Components/Settlements/PoolBoost/StardustBoostCard";
import { ValorBoostCard } from "Components/Settlements/PoolBoost/ValorBoostCard";
import { LoyaltyPointType, Region } from "Infrastructure/Api/Api";
import { useSearchParams } from "Navigation/useSearchParams";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Admin.SettlementPoolBoosts;
const REGION_PARAM = "region";

export const SettlementPoolBoostsContent = () => {
  const { t } = useResource();
  const { data, isLoading, isError } = useGetSettlementPoolBoostsQuery();
  const [searchParams, setSearchParams] = useSearchParams();

  if (isLoading) {
    return <CircularProgress />;
  }

  if (isError || !data) {
    return <Alert severity="error">{t(RESOURCES.LoadingError)}</Alert>;
  }

  // Only show tabs for regions that actually have boosts, in canonical enum order.
  const availableRegions = Object.values(Region).filter((region) =>
    data.boosts.some((b) => b.region === region),
  );

  const requestedRegion = searchParams.get(REGION_PARAM) as Region | null;
  const activeRegion =
    requestedRegion && availableRegions.includes(requestedRegion)
      ? requestedRegion
      : availableRegions[0];

  const stardustBoost = data.boosts.find(
    (b) =>
      b.region === activeRegion &&
      b.loyaltyPointType === LoyaltyPointType.Stardust,
  );
  const valorBoost = data.boosts.find(
    (b) =>
      b.region === activeRegion &&
      b.loyaltyPointType === LoyaltyPointType.Valor,
  );

  return (
    <Paper variant="outlined" sx={{ borderRadius: "10px" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeRegion}
          onChange={(_e, region: Region) =>
            setSearchParams({ [REGION_PARAM]: region })
          }
          variant="scrollable"
        >
          {availableRegions.map((region) => (
            <Tab
              key={region}
              value={region}
              label={t(RESOURCES.Regions[region])}
            />
          ))}
        </Tabs>
      </Box>

      {/* key forces remount per region so the forms re-init with that region's values */}
      <Stack gap={3} sx={{ p: 3 }}>
        {stardustBoost && (
          <StardustBoostCard
            key={`stardust-${activeRegion}`}
            region={activeRegion}
            currencyCode={stardustBoost.boostAmount.currencyCode}
            boostAmount={stardustBoost.boostAmount.value}
            settlementPoolBoostID={stardustBoost.settlementPoolBoostID}
          />
        )}
        {valorBoost && (
          <ValorBoostCard
            key={`valor-${activeRegion}`}
            region={activeRegion}
            currencyCode={valorBoost.boostAmount.currencyCode}
            boostAmount={valorBoost.boostAmount.value}
            hunterPoolRatioPercent={valorBoost.hunterPoolRatioPercent ?? 50}
            settlementPoolBoostID={valorBoost.settlementPoolBoostID}
          />
        )}
      </Stack>
    </Paper>
  );
};
